import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../api/useFetchObjects";
import useDelete from "../../api/useDelete";
import { useAuth } from "../../context/AuthContext";
import ErrorPage from "../Components/ErrorPage";
import AccountTransferAdd from "./AccountTransferAdd";
import AccountTransferEdit from "./AccountTransferEdit";
import AccountTransferView from "./AccountTransferView";
import { formatJalaliDate } from "../utils/dateFormat";

const AccountTransferIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const [openView, setOpenView] = useState(false);
  const [viewData, setViewData] = useState(null);

  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["account-transfers", searchTerm, page, rowsPerPage],
    `shop/account-transfers/?search=${searchTerm}&page=${page}&page_size=${rowsPerPage}`,
    token
  );
  const { handleDelete, ConfirmDialog } = useDelete("shop/account-transfers", token);

  useEffect(() => {
    refetch();
  }, [searchTerm, page, rowsPerPage, refetch, openAdd]);

  const rows = data?.results || data || [];
  const totalCount = data?.count || rows.length;

  if (isError) {
    return <ErrorPage message={t("accountTransfer.failedToLoad")} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h4">{t("accountTransfer.listTitle")}</Typography>
        <Button variant="contained" onClick={() => setOpenAdd(true)}>
          {t("accountTransfer.addNew")}
        </Button>
      </Box>

      <TextField
        fullWidth
        sx={{ mb: 2 }}
        placeholder={t("accountTransfer.searchPlaceholder")}
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setPage(1);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>{t("accountTransfer.index")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("accountTransfer.fromAccount")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("accountTransfer.toAccount")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("accountTransfer.amount")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("accountTransfer.exchangeRate")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("accountTransfer.convertedAmount")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("accountTransfer.reference")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("accountTransfer.transferDate")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("accountTransfer.action")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, i) => (
                  <TableRow key={`skeleton-${i}`}>
                    {Array.from(new Array(9)).map((__, j) => (
                      <TableCell key={`${i}-${j}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : rows.map((row, i) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{(page - 1) * rowsPerPage + i + 1}</TableCell>
                    <TableCell>{row.from_account_name || "-"}</TableCell>
                    <TableCell>{row.to_account_name || "-"}</TableCell>
                    <TableCell>{row.amount}</TableCell>
                    <TableCell>{row.exchange_rate}</TableCell>
                    <TableCell>{row.converted_amount}</TableCell>
                    <TableCell>{row.reference || "-"}</TableCell>
                    <TableCell>{formatJalaliDate(row.transfer_date)}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => {
                          setViewData(row);
                          setOpenView(true);
                        }}
                      >
                        View
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => {
                          setEditData(row);
                          setOpenEdit(true);
                        }}
                      >
                        {t("accountTransfer.edit", "Edit")}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(row.id)}
                      >
                        {t("accountTransfer.delete")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container justifyContent="center" alignItems="center" padding="10px">
        <Box display="flex" alignItems="center" gap={1}>
          <Typography>{t("accountTransfer.rowsPerPage")}</Typography>
          <FormControl size="small">
            <Select value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage)}
          page={page}
          onChange={(_, p) => setPage(p)}
          variant="outlined"
          color="primary"
          sx={{ ml: 2 }}
        />
      </Grid>

      <ConfirmDialog />
      {openAdd && <AccountTransferAdd open={openAdd} onClose={() => setOpenAdd(false)} token={token} />}
      {openEdit && <AccountTransferEdit open={openEdit} onClose={() => setOpenEdit(false)} token={token} editData={editData} />}
      {openView && <AccountTransferView open={openView} onClose={() => setOpenView(false)} data={viewData} />}
    </Box>
  );
};

export default AccountTransferIndex;
